import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { RootLayout } from '@/components/layout/RootLayout';
import { ProtectedLayout } from '@/components/layout/ProtectedLayout';
import { RequireAuth } from './RequireAuth';
import { RouterRoot } from './RouterRoot';

/**
 * Duas ramificações no topo: o que é público e o que passa pela guarda.
 *
 * `RequireAuth` é rota-layout **sem path**, acima de todos os outros layouts.
 * Precisa ser aqui: `/catalogo/:slug` e `/auto-servico/:code` têm layouts
 * próprios no topo da árvore, e uma guarda dentro do `RootLayout` não os
 * alcançaria — deixaria abertas exatamente as duas superfícies que este
 * fechamento existe para cobrir.
 *
 * A que se esquece é `/self-service/:code`: ele parece um detalhe de
 * compatibilidade, mas é um redirect para dentro do auto-serviço, e deixá-lo
 * fora da guarda daria uma porta lateral para ele.
 *
 * **A vitrine mora na RAIZ.** Ela não tem namespace, e a marca tem o seu
 * (`/marca/:slug`) — com isso não existe segmento dinâmico competindo na raiz,
 * e a lista de slugs reservados que a vitrine mantinha deixou de ser
 * necessária. Ver `features/storefront/utils/slugs.ts`.
 *
 * Todas seguem `lazy`. A guarda mora na rota-pai e decide **antes** de o chunk
 * do filho ser pedido, então fechar o app não custou carregamento antecipado.
 */
const routes: RouteObject[] = [
  {
    // O acesso tem layout próprio: `AuthLayout` é tela cheia, sem `Topbar` e
    // sem rodapé. Os dois pertencem ao `RootLayout` e navegam a vitrine —
    // justamente o que quem está no login ainda não pode abrir.
    element: <AuthLayout />,
    children: [
      {
        path: '/login',
        lazy: async () => {
          const { Login } = await import('@/pages/Login');
          return { Component: Login };
        },
      },
      {
        // O autocadastro vive no MESMO layout do login, e fora da guarda: é o
        // mesmo momento da jornada, e quem chega aqui não tem conta — é o que
        // esta tela existe para criar.
        path: '/onboarding',
        lazy: async () => {
          const { Onboarding } = await import(
            '@/features/onboarding/pages/Onboarding'
          );
          return { Component: Onboarding };
        },
      },
    ],
  },

  {
    element: <RootLayout />,
    children: [
      {
        // Termos e privacidade: públicos, fora da guarda. O wizard de
        // boas-vindas abre estes em aba nova, e quem ainda não aceitou — ou
        // nem tem conta — precisa conseguir ler antes.
        path: '/termos',
        lazy: async () => {
          const { TermsPage } = await import('@/features/legal/pages/LegalPages');
          return { Component: TermsPage };
        },
      },
      {
        path: '/privacidade',
        lazy: async () => {
          const { PrivacyPage } = await import('@/features/legal/pages/LegalPages');
          return { Component: PrivacyPage };
        },
      },
      {
        // O 404 do app inteiro, e ele fica FORA da guarda: erro de digitação de
        // quem está deslogado não pode virar ida ao login.
        //
        // Era o `NotFound` genérico, com um segundo 404 dentro da vitrine. Com
        // a vitrine na raiz os dois viraram o mesmo padrão (`/*`) e passaram a
        // competir — e todo caminho desconhecido é, agora, um caminho da
        // vitrine. Ficou o que rastreia.
        path: '*',
        lazy: async () => {
          const { StorefrontNotFound } = await import(
            '@/features/storefront/pages/StorefrontNotFound'
          );
          return { Component: StorefrontNotFound };
        },
      },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <ProtectedLayout />,
        children: [
          {
            // A vitrine na raiz. O layout dela (menu de categorias, gaveta da
            // sacola, faixa fixa) envolve tudo o que é vitrine, e nada mais.
            path: '/',
            lazy: async () => {
              const { StorefrontLayout } = await import(
                '@/features/storefront/components/StorefrontLayout'
              );
              return { Component: StorefrontLayout };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { StorefrontHome } = await import(
                    '@/features/storefront/pages/StorefrontHome'
                  );
                  return { Component: StorefrontHome };
                },
              },
              {
                path: 'categorias',
                lazy: async () => {
                  const { StorefrontAll } = await import(
                    '@/features/storefront/pages/StorefrontAll'
                  );
                  return { Component: StorefrontAll };
                },
              },
              {
                path: 'categorias/:categoria',
                lazy: async () => {
                  const { StorefrontCatalog } = await import(
                    '@/features/storefront/pages/StorefrontCatalog'
                  );
                  return { Component: StorefrontCatalog };
                },
              },
              {
                // Sempre acessível por URL — o toggle do manager esconde só o
                // link do menu e o banner da home.
                path: 'pronta-entrega',
                lazy: async () => {
                  const { StorefrontReadyDelivery } = await import(
                    '@/features/storefront/pages/StorefrontReadyDelivery'
                  );
                  return { Component: StorefrontReadyDelivery };
                },
              },
              {
                path: 'produto/:productId',
                lazy: async () => {
                  const { ProductDetail } = await import(
                    '@/features/storefront/pages/ProductDetail'
                  );
                  return { Component: ProductDetail };
                },
              },
              {
                path: 'pedidos',
                lazy: async () => {
                  const { OrdersPage } = await import(
                    '@/features/storefront/pages/OrdersPage'
                  );
                  return { Component: OrdersPage };
                },
              },
              {
                path: 'pedidos/:orderId',
                lazy: async () => {
                  const { OrderDetail } = await import(
                    '@/features/storefront/pages/OrderDetail'
                  );
                  return { Component: OrderDetail };
                },
              },
              {
                path: 'carrinho',
                lazy: async () => {
                  const { CartPage } = await import(
                    '@/features/storefront/pages/CartPage'
                  );
                  return { Component: CartPage };
                },
              },
              {
                // A marca tem namespace, e é ele que elimina a competição com
                // os segmentos estáticos acima. Sem `/marca`, uma marca
                // chamada "pedidos" ficaria inalcançável — em silêncio.
                path: 'marca/:marca',
                lazy: async () => {
                  const { StorefrontCatalog } = await import(
                    '@/features/storefront/pages/StorefrontCatalog'
                  );
                  return { Component: StorefrontCatalog };
                },
              },
              {
                path: 'marca/:marca/:categoria',
                lazy: async () => {
                  const { StorefrontCatalog } = await import(
                    '@/features/storefront/pages/StorefrontCatalog'
                  );
                  return { Component: StorefrontCatalog };
                },
              },
              {
                // Endereços antigos. Não é remover o prefixo — a marca mudou de
                // forma, e o componente decide. Ver `LegacyStorefrontRedirect`.
                path: 'e-commerce/*',
                lazy: async () => {
                  const { LegacyStorefrontRedirect } = await import(
                    '@/features/storefront/components/LegacyStorefrontRedirect'
                  );
                  return { Component: LegacyStorefrontRedirect };
                },
              },
            ],
          },
          {
            // A operação tem endereço próprio desde que a raiz virou a vitrine.
            // É para cá que o `ExperienceToggle` leva.
            path: '/operacao',
            lazy: async () => {
              const { Home } = await import('@/pages/Home');
              return { Component: Home };
            },
          },
        ],
      },
      {
        path: '/auto-servico/:code',
        lazy: async () => {
          const { SelfServiceLayout } = await import(
            '@/features/self-service/components/SelfServiceLayout'
          );
          return { Component: SelfServiceLayout };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { SelfServicePage } = await import(
                '@/features/self-service/pages/SelfServicePage'
              );
              return { Component: SelfServicePage };
            },
          },
        ],
      },
      {
        path: '/self-service/:code',
        lazy: async () => {
          const { LegacyRedirect } = await import(
            '@/features/self-service/components/LegacyRedirect'
          );
          return { Component: LegacyRedirect };
        },
      },
      {
        path: '/catalogo/:slug',
        lazy: async () => {
          const { CatalogLayout } = await import(
            '@/features/catalog/components/CatalogLayout'
          );
          return { Component: CatalogLayout };
        },
        children: [
          {
            index: true,
            lazy: async () => {
              const { CatalogPage } = await import(
                '@/features/catalog/pages/CatalogPage'
              );
              return { Component: CatalogPage };
            },
          },
          {
            path: 'kits/:kitId',
            lazy: async () => {
              const { KitDetailPage } = await import(
                '@/features/catalog/pages/KitDetailPage'
              );
              return { Component: KitDetailPage };
            },
          },
        ],
      },
    ],
  },
];

// `RouterRoot` envolve as ramificações só para montar o `ScrollRestoration`.
const router = createBrowserRouter([
  { element: <RouterRoot />, children: routes },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
